import React from "react";
import { Link } from "react-router-dom";

const DEFAULT_STEPS = [
  {
    id: "ingredients",
    title: "1. Lägg till ingredienser, produkter eller recept",
    intro:
      "Varje rad kan vara en råvara, en inköpt produkt eller ett recept du redan byggt.",
    bullets: [
      "Välj typ i första kolumnen (Ingrediens / Produkt / Recept).",
      "Sök efter råvaran eller ange GTIN för att hitta produkten automatiskt.",
      {
        text: "Välj enhet (t.ex. g, kg, ml) och mata in mängden. Systemet räknar om till gram.",
        note:
          "OBS! Om typ är Produkt/Recept väljs enheten automatiskt baserat på produktens eller receptets standard.",
      },
      "Sätt ursprungsland och markera om ingrediensen är ekologisk.",
      "Använd rubrik och kommentar för att gruppera (t.ex. ‘Sås’, ‘Topping’).",
      "Öppna detaljer (ikonen med penna) om du behöver lägga till noteringar eller rubriker.",
    ],
    example:
      "Exempel: Välj ‘Produkt’, sök ‘Vegansk burgare’, sätt enhet g och mängd 160. Lägg till rubrik ‘Protein’.",
  },
  {
    id: "sustainability",
    title: "2. Kontrollera hållbarhetsvärden",
    intro:
      "När ingredienslistan är klar uppdateras CO₂e och övriga poäng automatiskt.",
    bullets: [
      "Justera ‘Viktförlust’ för att hålla CO₂e/kg realistiskt efter tillagning.",
      "Granska korten Miljö, Hälsa och Rättvisa för att hitta förbättringsmöjligheter.",
      "Öppna ‘Näringsvärde’ eller ‘Parametrar’ om du vill se detaljerad statistik.",
    ],
    example:
      "Exempel: Sätt viktförlust till 12 % för en gryta som kokar länge och bekräfta att CO₂e/kg ökar rimligt.",
  },
  {
    id: "description",
    title: "3. Skriv beskrivning av rätten",
    intro:
      "Använd textfältet för att berätta vad som gör rätten unik.",
    bullets: [
      "Beskriv smak, allergener eller serveringstips.",
      "Använd punktknapparna för att snabbt skapa snygga listor.",
      "Börja varje rad med • eller - om du vill att systemet fyller i punkt format automatiskt.",
    ],
    example:
      "Exempel: ‘• Serveras med picklad rödlök’ + ‘• Passar bra som veckans lunch’",
  },
  {
    id: "meals",
    title: "4. Ställ in måltidstyper och dagar",
    intro:
      "Måltidsinställningarna hjälper köket och rapporterna att veta när rätten används.",
    bullets: [
      "Kryssa i om rätten är frukost, lunch, middag osv.",
      "Markera vilka veckodagar rätten visas på menyn.",
      "Ange retroaktiv data – totalt antal sålda portioner och ev. svinn – för att få bättre analyser.",
    ],
    example:
      "Exempel: Lunch + Middag, visa Mån–Fre, 120 portioner per vecka, 2 % svinn.",
  },
  {
    id: "reports",
    title: "5. Exportera och dela PDF-rapporten",
    intro:
      "Skapa en rapport som visar ingredienslista, klimatvärden och näringsinformation.",
    bullets: [
      "Klicka på PDF-knappen för att generera filen.",
      "Kontrollera att rubriker, beskrivning och hållbarhet visas korrekt.",
      "Dela rapporten med köket, gäster eller hållbarhetsteamet.",
    ],
    example:
      "Exempel: Exportera PDF och skicka till restaurangchefen varje vecka inför menyuppdatering.",
  },
  {
    id: "publish",
    title: "6. Granska rapporter och publicera (kommande)",
    intro:
      "Snart kan du publicera rätten direkt från Menuwiz och koppla den till externa system.",
    bullets: [
      "Följ dina interna processer för publicering tills funktionen är aktiv.",
      "Behöver du uppdatera rätten senare? All data finns sparad här.",
    ],
    example:
      "Kommer snart: aktivera publicering, koppla till kassasystem och spåra status.",
  },
];

const WorkflowDrawer = ({
  open,
  onClose,
  title = "Arbetsflöde för rätten",
  description = "Följ stegen för att säkerställa att allt är komplett innan publicering.",
  steps = DEFAULT_STEPS,
  footerButtons,
}) => {
  const buttons = (footerButtons ?? [
    { type: "button", label: "Jag är klar", onClick: onClose },
  ]).filter(Boolean);

  return (
    <div
      className={`fixed inset-0 z-[70] transition ${
        open ? "pointer-events-auto" : "pointer-events-none"
      }`}
    >
      <div
        className={`absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-md transform bg-white shadow-2xl transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              {description && (
                <p className="text-sm text-gray-500">{description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="ml-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition hover:bg-gray-200"
              aria-label="Stäng arbetsflöde"
            >
              ×
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <ol className="space-y-6">
              {steps.map((step, index) => (
                <li
                  key={step.id}
                  className="rounded-2xl border border-gray-100 bg-gray-50 p-5"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-green">
                        {`Steg ${index + 1}`}
                      </p>
                      <h3 className="mt-1 text-base font-semibold text-gray-900">
                        {step.title}
                      </h3>
                    </div>
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-secondary-green/10 text-sm font-semibold text-secondary-green">
                      {index + 1}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">{step.intro}</p>
                  <ul className="mt-3 space-y-1 text-sm text-gray-600">
                    {step.bullets.map((bullet, bulletIndex) => {
                      const bulletText = typeof bullet === "string" ? bullet : bullet.text;
                      const bulletNote = typeof bullet === "string" ? null : bullet.note;
                      return (
                        <li key={bulletIndex} className="flex flex-col gap-2">
                          <div className="flex items-start gap-2">
                            <span className="mt-1 inline-flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-secondary-green"></span>
                            <span>{bulletText}</span>
                          </div>
                          {bulletNote && (
                            <div className="ml-4 text-xs text-gray-500">{bulletNote}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {step.example && (
                    <p className="mt-3 rounded-xl bg-white px-4 py-3 text-xs text-gray-500">
                      {step.example}
                    </p>
                  )}
                </li>
              ))}
            </ol>

            {buttons.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2 text-xs">
                {buttons.map((button, index) =>
                  button.type === "link" ? (
                    <Link
                      key={index}
                      to={button.to}
                      className="inline-flex items-center gap-1 rounded-full bg-secondary-green/15 px-3 py-1.5 font-semibold text-secondary-green transition hover:bg-secondary-green/25"
                      onClick={() => {
                        button.onClick?.();
                        onClose();
                      }}
                    >
                      {button.label}
                      {button.icon && (
                        <img src={button.icon} alt="" className="h-3 w-3" />
                      )}
                    </Link>
                  ) : (
                    <button
                      key={index}
                      type="button"
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 font-semibold text-gray-600"
                      onClick={() => {
                        button.onClick?.();
                        if (!button.onClick) onClose();
                      }}
                    >
                      {button.label}
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default WorkflowDrawer;

